import { capitalizeString } from '@/helpers/general';

export const CARLIE_CONTEXT_DROPPED_AFTER_TRANSFER = "dropped_after_transfer";
export const CARLIE_CONTEXT_LIVE_HANDOFF = "live_handoff";
export const CARLIE_CONTEXT_MISSED_CALL = "missed_call";

const CARLIE_CONTEXT_DESCRIPTIONS: Record<string, string> = {
    [CARLIE_CONTEXT_DROPPED_AFTER_TRANSFER]: "Carlie transferred this caller, but they hung up before an agent answered",
    [CARLIE_CONTEXT_LIVE_HANDOFF]: "Carlie transferred this caller to you live",
    [CARLIE_CONTEXT_MISSED_CALL]: "Carlie took this call but was unable to resolve the customer's request",
};

const CARLIE_CONTEXT_LABELS: Record<string, string> = {
    [CARLIE_CONTEXT_DROPPED_AFTER_TRANSFER]: "After Carlie",
    [CARLIE_CONTEXT_LIVE_HANDOFF]: "Via Carlie",
    [CARLIE_CONTEXT_MISSED_CALL]: "Carlie",
};

const CARLIE_HANDOFF_REASON_LABELS: Record<string, string> = {
    appointment_interest: "Appointment interest",
    caller_requested_human: "Caller requested a person",
    out_of_scope: "Out of scope",
    technical_failure: "Technical failure",
};

// Only the Carlie outcomes that change how an agent should open the call, in the order an agent
// needs them: what the caller asked for, what Carlie may have gotten wrong, then what is still
// open. Keyed by outcome item type description because ids differ per environment. Frustration
// with the AI is left out because sentiment and escalation already say how the caller feels,
// "attempted to manipulate the ai" because it would prime the agent against the caller on a
// guess, and "needed human follow-up" because every transfer does.
const CARLIE_AGENT_FLAGS: Array<{ description: string, icon: string, label: string }> = [
    { description: "customer asked to speak with a human", icon: "mdi-account-voice", label: "Asked for a person" },
    { description: "ai acted on the wrong existing appointment", icon: "mdi-calendar-alert", label: "Carlie used the wrong appointment" },
    { description: "ai stated unsupported information or guessed", icon: "mdi-alert-circle-outline", label: "Carlie may have guessed" },
    { description: "ai got a customer detail wrong", icon: "mdi-account-alert-outline", label: "Carlie got a detail wrong" },
    { description: "customer asked something the ai could not answer", icon: "mdi-help-circle-outline", label: "Unanswered question" },
    { description: "customer requested something outside the ai's capabilities", icon: "mdi-cancel", label: "Asked for something Carlie can't do" },
    { description: "ai asked for information the customer already gave", icon: "mdi-repeat", label: "Already repeated their info" },
];

type CarlieVoiceMailRecording = {
    ai_communication_id?: number | null;
    ai_communication_provider?: string | null;
    purpose?: string;
};

export function isCarlieRelated(voiceMailRecording?: CarlieVoiceMailRecording | null): boolean {
    return !!voiceMailRecording?.ai_communication_id && voiceMailRecording.ai_communication_provider === "carlie";
}

// A Carlie missed call is one Carlie took and handed off; any other Carlie-linked recording is a
// caller who dropped after Carlie transferred them to the queue.
export function carlieContext(voiceMailRecording?: CarlieVoiceMailRecording | null): string | null {
    if (!isCarlieRelated(voiceMailRecording)) {
        return null;
    }
    if (voiceMailRecording?.purpose === "carlie") {
        return CARLIE_CONTEXT_MISSED_CALL;
    }
    return CARLIE_CONTEXT_DROPPED_AFTER_TRANSFER;
}

// What the customer profile's Carlie tab shows: the live transfer the agent accepted, else the
// Carlie missed call they are returning. Details are only cached, and analysis only pushed, for a
// live transfer.
export function carlieTabSource(
    liveHandoff?: { aiCommunicationId: number, analysis?: any, details?: any } | null,
    voiceMailRecording?: CarlieVoiceMailRecording | null,
): { aiCommunicationId: number, analysis: any, context: string, details: any } | null {
    if (liveHandoff?.aiCommunicationId > 0) {
        return {
            aiCommunicationId: liveHandoff.aiCommunicationId,
            analysis: liveHandoff.analysis || null,
            context: CARLIE_CONTEXT_LIVE_HANDOFF,
            details: liveHandoff.details || null,
        };
    }
    const context = carlieContext(voiceMailRecording);
    if (!context) {
        return null;
    }
    return { aiCommunicationId: voiceMailRecording.ai_communication_id, analysis: null, context, details: null };
}

export function carlieContextDescription(context?: string | null): string {
    return CARLIE_CONTEXT_DESCRIPTIONS[context] || CARLIE_CONTEXT_DESCRIPTIONS[CARLIE_CONTEXT_MISSED_CALL];
}

export function carlieContextLabel(context?: string | null): string {
    return CARLIE_CONTEXT_LABELS[context] || "";
}

export function carlieHandoffReasonLabel(reason?: string | null): string {
    // transfer_call only says that Carlie transferred, which every handoff already did.
    if (!reason || reason === "transfer_call") {
        return "";
    }
    if (CARLIE_HANDOFF_REASON_LABELS[reason]) {
        return CARLIE_HANDOFF_REASON_LABELS[reason];
    }
    const words = reason.replaceAll("_", " ");
    return words.charAt(0).toUpperCase() + words.slice(1);
}

export function carlieHandoffSummary(analysis?: any): string {
    const item = (analysis?.assessment_items || []).find((item) => item.llm_assessment_type === "handoff_summary");
    return item?.summary || "";
}

// Assessment items are asked as yes/no questions, so a summary can open with the bare answer
// ("No, the customer became frustrated..."), which reads oddly once it stands on its own.
export function carlieItemSummary(summary?: string | null): string {
    return capitalizeString((summary || "").trim().replace(/^(yes|no)\s*[,.:;!-]\s*/i, ""));
}

export function carliePromises(analysis?: any): any[] {
    // The promise item is always scored, and an empty summary means no promise was made.
    return (analysis?.assessment_items || []).filter((item) => item.llm_assessment_type === "promise" && item.summary);
}

// Pulse's summary covers the whole Carlie leg, so it wins once the analysis lands.
export function carlieSummary(analysis?: any, handoff?: any): string {
    if (analysis?.status === "completed" && analysis?.summary) {
        return analysis.summary;
    }
    return handoff?.summary || "";
}

// The one line an agent reads before answering a live transfer: the analysis's handoff headline,
// or Carlie's intent until the analysis has one. The handoff summary is written for someone
// calling the customer back later, so it is only ever detail under the headline.
export function carlieHeadline(analysis?: any, handoff?: any): { fromIntent: boolean, text: string } {
    const item = (analysis?.assessment_items || []).find((item) => item.llm_assessment_type === "handoff_headline");
    const headline = (item?.summary || "").trim();
    if (headline) {
        return { fromIntent: false, text: headline };
    }
    const intent = (handoff?.caller_intent || "").trim();
    return { fromIntent: !!intent, text: intent };
}

export function carlieAgentFlags(analysis?: any): Array<{ icon: string, label: string }> {
    const outcomes = analysis?.outcomes || [];
    return CARLIE_AGENT_FLAGS
        .filter((flag) => outcomes.includes(flag.description))
        .map(({ icon, label }) => ({ icon, label }));
}

// The matched customer comes from the phone number, so the person calling can be someone else on
// the account. Names that share a first name are treated as the same person.
export function carlieCallerName(callerName?: string | null, customerName?: string | null): { account: string, name: string } {
    const caller = (callerName || "").trim();
    const customer = (customerName || "").trim();
    if (!caller || !customer) {
        return { account: "", name: customer || caller };
    }
    const firstName = (name: string) => name.split(/\s+/)[0].toLowerCase();
    if (firstName(caller) === firstName(customer)) {
        return { account: "", name: customer };
    }
    return { account: customer, name: caller };
}
